import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type TalkJS from 'talkjs';

import { colors } from '@styleseat/brand';
import { Box } from '@styleseat/ui';
import analytics from '../../../modules/analytics';
import ChatManager from '../../../modules/chat/ChatManager';
import { IRootDispatch, RootState } from '../../../store/models';
import {
  useCurrentChatSession,
  useFeatureFlagRedux,
  useGoBack,
  useOnce,
  useParams,
  useUserStateRedux,
} from '../../../hooks';
import { UnconnectedNavigateBack } from '../../shared/NavigateBack';
import BlackLionLayout from '../BlackLionLayout';
import { HOME_ROUTE } from '../../../route-names';
import { TalkjsChatbox } from './TalkjsChatbox';
import { ProviderLayout } from '../../layouts/ProviderLayout/ProviderLayout';
import { Absolute } from '../../ui';
import {
  CHAT_GENERAL_SWITCH,
  CHAT_WELCOME_MESSAGE_SENT,
  ChatRoles,
} from '../../../modules/chat/chatTypes';

type ProChatLayoutProps = {
  onGoBack: () => void;
  providerId?: number;
};

const ProChatLayout: React.FC<ProChatLayoutProps> = ({
  children,
  onGoBack,
  providerId,
}) => (
  <ProviderLayout providerId={providerId} showMobileNavBar={false}>
    <Absolute top={12} left={4}>
      <UnconnectedNavigateBack onPress={onGoBack} />
    </Absolute>
    {children}
  </ProviderLayout>
);

const ClientChatLayout: React.FC = ({
  children,
}) => (
  <BlackLionLayout showHeader>
    <Box
      borderColor={colors.lightGrey}
      borderTopWidth={1}
      borderStyle="solid"
      flexDirection="column"
      flexBasis="100%"
    >
      {children}
    </Box>
  </BlackLionLayout>
);

const ChatPage: React.FC = () => {
  const dispatch = useDispatch<IRootDispatch>();
  const goBack = useGoBack();
  const params = useParams();
  const {
    chatId, isWelcomeMessage, requiredRole: requiredRoleParam, deep_link_sub2,
  } = params;
  const requiredRole: string = requiredRoleParam || deep_link_sub2 || ChatRoles.Client;

  const isChatEnabled = useFeatureFlagRedux(
    CHAT_GENERAL_SWITCH,
  ).enabled;
  const [
    welcomeMessageSent, setWelcomeMessageSent, welcomeMessageSentLoaded,
  ] = useUserStateRedux(CHAT_WELCOME_MESSAGE_SENT);
  const currentUser = useSelector((state: RootState) => state.user);
  const getChatSession = useCurrentChatSession(currentUser, requiredRole as ChatRoles);
  const providerID = useSelector((state: RootState) => state?.user?.providerId);
  const isUserPro = requiredRole === ChatRoles.Provider;

  const [chatSession, setChatSession] = useState<TalkJS.Session>(null);
  const [currentConversation, setCurrentConversation] = useState<TalkJS.ConversationBuilder>(
    ChatManager.conversation,
  );
  const [canMountChatbox, setCanMountChatbox] = useState<boolean>(false);
  const [firstReplySent, setFirstReplySent] = useState<boolean>(false);

  const handleGoBack = () => {
    goBack(HOME_ROUTE);
  };

  useEffect(() => {
    if (isUserPro) {
      analytics.track('chat_page_landed', {
        provider_id: providerID,
        conversation_id: chatId,
      });
    } else {
      analytics.track('chat_page_landed', {
        client_user_id: currentUser.userId,
        conversation_id: chatId,
      });
    }
    return () => {
      ChatManager.cleanUp();
    };
  }, []);

  const sendInitialMessageReply = () => currentConversation.sendMessage('🎉');

  const sendInitialMessageReplyTask = async () => {
    if (!firstReplySent) {
      try {
        await sendInitialMessageReply();
        setWelcomeMessageSent(true);
        setFirstReplySent(true);
      } catch (e) {
        setTimeout(async () => {
          await sendInitialMessageReplyTask();
        }, 2000);
      }
    }
  };

  useOnce(() => {
    if (!welcomeMessageSent) {
      sendInitialMessageReplyTask();
    }
  }, (currentConversation
    && chatSession
    && chatId
    && welcomeMessageSentLoaded
    && (isWelcomeMessage === 'true')
    && (isUserPro && providerID)) || null);

  useOnce(async () => {
    setChatSession(await getChatSession());
  }, (currentUser?.is_anon === false) || null);

  useOnce(async () => {
    if (!currentConversation) {
      await ChatManager.setCurrentConversation(chatId);
      setCurrentConversation(ChatManager.conversation);
    }
  }, (chatId && chatSession) || null);

  useOnce(() => {
    if (!isChatEnabled || (isUserPro && !providerID)) {
      // Go back if chat is disabled or if a client tries to access a pro chat
      handleGoBack();
    }
  }, !(isChatEnabled === undefined || isChatEnabled === null));

  const ChatLayout: (React.FC | React.FC<ProChatLayoutProps>) = isUserPro
    ? ProChatLayout : ClientChatLayout;

  useEffect(() => {
    if (currentConversation && chatSession?.createChatbox) {
      setCanMountChatbox(true);
      dispatch.loader.setIsLoading(false);
    }
  }, [
    currentConversation,
    chatSession,
  ]);

  return (
    <ChatLayout onGoBack={handleGoBack} providerId={isUserPro ? providerID : undefined}>
      <TalkjsChatbox
        chatSession={chatSession}
        currentConversation={currentConversation}
        canMount={canMountChatbox}
        isUserPro={isUserPro}
      />
    </ChatLayout>
  );
};

export default ChatPage;
